<template>
  <div class="configuration-container">
    <div class="configuration-heading">
      <div>
        <span class="eyebrow">ADMIN SETTINGS</span>
        <h1>Configuration</h1>
        <p>Choose which sales sites are used by {{ studioName || 'this studio' }}.</p>
      </div>
      <v-chip v-if="studioName" color="primary" variant="tonal" size="large">
        {{ studioName }}
      </v-chip>
    </div>

    <v-alert v-if="error" type="error" variant="tonal" class="configuration-alert">
      {{ error }}
    </v-alert>

    <v-card class="configuration-card sites-card" elevation="1">
      <v-card-title class="configuration-card-title">
        <div>
          <span>Studio Sites</span>
          <small>Recommended sites begin enabled, but every site can be changed for this studio.</small>
        </div>
        <v-btn color="primary" :loading="saving" :disabled="loading || !hasChanges" @click="saveSites">
          Save Changes
        </v-btn>
      </v-card-title>

      <v-progress-linear v-if="loading" indeterminate color="primary" />

      <v-card-text v-else class="configuration-card-text sites-content">
        <section>
          <div class="section-heading">
            <h2>Recommended Sites</h2>
            <span>Commonly used by most studios</span>
          </div>
          <div class="site-grid">
            <label v-for="site in recommendedSites" :key="site.name" class="site-row site-row--recommended">
              <input v-model="site.enabled" class="site-checkbox" type="checkbox">
              <div class="site-copy">
                <div class="site-name-line">
                  <strong>{{ site.displayName }}</strong>
                  <span class="site-color-swatch" :style="{ backgroundColor: siteColor(site) }" :title="`${site.displayName}: ${siteColor(site)}`"></span>
                </div>
                <small>{{ site.cycleLabel }}</small>
              </div>
              <span class="site-status site-status--recommended">Recommended</span>
            </label>
          </div>
        </section>

        <section>
          <div class="section-heading">
            <h2>Additional Sites</h2>
            <span>Enable only where the studio actively works</span>
          </div>
          <div class="site-grid">
            <label v-for="site in additionalSites" :key="site.name" class="site-row">
              <input v-model="site.enabled" class="site-checkbox" type="checkbox">
              <div class="site-copy">
                <div class="site-name-line">
                  <strong>{{ site.displayName }}</strong>
                  <span class="site-color-swatch" :style="{ backgroundColor: siteColor(site) }" :title="`${site.displayName}: ${siteColor(site)}`"></span>
                </div>
                <small :class="{ 'period-pending': !site.periodConfigured }">{{ site.cycleLabel }}</small>
              </div>
              <span class="site-status" :class="site.periodConfigured ? 'site-status--mapped' : 'site-status--pending'">
                {{ site.periodConfigured ? 'Period mapped' : site.pendingLabel }}
              </span>
            </label>
          </div>
        </section>

        <v-alert type="info" variant="tonal" density="compact" class="sites-note">
          Disabled sites are removed from this studio’s active selection. Sites marked “Needs period info” will not be connected to financial calculations until their payment calendar is confirmed.
        </v-alert>
      </v-card-text>
    </v-card>

    <v-card class="configuration-card" elevation="1">
      <v-card-title class="configuration-card-title simple-title">Manage Monitors</v-card-title>
      <v-card-text class="configuration-card-text">
        Monitor assignments live on the Models page under the models table.
      </v-card-text>
      <v-card-actions class="configuration-card-actions">
        <v-btn color="primary" variant="tonal" @click="$router.push('/models')">Open Models</v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import secureAxios from '@/utils/secureAxios';
import { toast } from 'vue3-toastify';
import { getSiteColor } from '@/constants/siteColors';

export default {
  name: 'Configuration',
  data() {
    return {
      error: '',
      loading: true,
      saving: false,
      sites: [],
      originalSiteState: {},
      studioName: '',
    };
  },
  computed: {
    recommendedSites() {
      return this.sites.filter(site => site.recommended);
    },
    additionalSites() {
      return this.sites.filter(site => !site.recommended);
    },
    hasChanges() {
      return this.sites.some(site => (
        Boolean(site.enabled) !== Boolean(this.originalSiteState[site.name])
      ));
    },
  },
  mounted() {
    this.loadSites();
  },
  methods: {
    siteColor(site) {
      return getSiteColor(site?.name || site?.displayName);
    },
    async loadSites() {
      this.loading = true;
      this.error = '';
      try {
        const { data } = await secureAxios.get('/api/config/sites');
        this.studioName = data?.studio?.name || '';
        this.sites = (data?.sites || []).map(site => ({ ...site, enabled: Boolean(site.enabled) }));
        this.originalSiteState = this.sites.reduce((state, site) => {
          state[site.name] = Boolean(site.enabled);
          return state;
        }, {});
      } catch (error) {
        this.error = error?.response?.data?.message || 'Unable to load studio site configuration';
      } finally {
        this.loading = false;
      }
    },
    async saveSites() {
      this.saving = true;
      try {
        const { data } = await secureAxios.put('/api/config/sites', {
          sites: this.sites.map(site => ({ name: site.name, enabled: Boolean(site.enabled) })),
        });
        toast.success(data?.message || 'Studio sites saved');
        await this.loadSites();
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Unable to save studio sites');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.configuration-container { max-width: 1040px; margin: 0 auto; padding: 18px 24px 48px; color: #203247; }
.configuration-heading { align-items: center; display: flex; justify-content: space-between; gap: 20px; }
.configuration-heading h1 { font-size: 27px; margin: 1px 0; }
.configuration-heading p { color: #64768a; margin: 3px 0; }
.eyebrow { color: #2f7db9; font-size: 12px; font-weight: 800; letter-spacing: .13em; }
.configuration-alert { margin-top: 18px; }
.configuration-card { border: 1px solid #dce4ec; border-radius: 8px; margin-top: 13px; overflow: hidden; }
.configuration-card-title { align-items: center; border-bottom: 1px solid #e1e7ed; display: flex; justify-content: space-between; padding: 11px 15px; }
.configuration-card-title > div { display: flex; flex-direction: column; gap: 2px; }
.configuration-card-title span, .simple-title { color: #17212b; font-weight: 800; }
.configuration-card-title small { color: #64768a; font-size: 12px; font-weight: 500; }
.configuration-card-text { color: #55606d; font-size: .92rem; padding: 13px 15px; }
.sites-content { display: grid; gap: 14px; }
.section-heading { align-items: end; display: flex; justify-content: space-between; margin-bottom: 8px; }
.section-heading h2 { color: #203247; font-size: 15px; margin: 0; }
.section-heading span { color: #7a8896; font-size: 11px; }
.site-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 7px; }
.site-row { align-items: center; background: #fff; border: 1px solid #e0e6ec; border-radius: 6px; cursor: pointer; display: grid; gap: 8px; grid-template-columns: 18px minmax(0, 1fr) auto; min-height: 48px; padding: 5px 9px; }
.site-row:hover { background: #f8fafc; border-color: #b9cbdc; }
.site-row--recommended { background: #f8fbf9; }
.site-checkbox { accent-color: #2f7db9; cursor: pointer; height: 16px; margin: 0; width: 16px; }
.site-copy { display: flex; flex-direction: column; gap: 2px; }
.site-name-line { display: flex; min-width: 0; align-items: center; gap: 7px; }
.site-color-swatch { width: 13px; height: 13px; flex: 0 0 13px; border: 1px solid rgba(25, 45, 62, .22); border-radius: 3px; box-shadow: inset 0 0 0 1px rgba(255, 255, 255, .28); }
.site-copy strong { color: #263b50; font-size: 13px; line-height: 1.15; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.site-copy small { color: #6a7b8c; font-size: 10px; line-height: 1.15; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.site-copy small.period-pending { color: #a56a00; }
.site-status { border-radius: 999px; font-size: 9px; font-weight: 700; line-height: 1; padding: 5px 7px; white-space: nowrap; }
.site-status--recommended { background: #e2f2e5; color: #27733a; }
.site-status--mapped { background: #e5f1fa; color: #27658f; }
.site-status--pending { background: #fff0d7; color: #976000; }
.sites-note { margin-top: 2px; }
.configuration-card-actions { justify-content: flex-end; padding: 0 16px 16px; }
@media (max-width: 700px) {
  .configuration-container { padding: 18px 12px 50px; }
  .configuration-heading, .configuration-card-title, .section-heading { align-items: stretch; flex-direction: column; }
  .configuration-card-title { gap: 12px; }
  .site-grid { grid-template-columns: 1fr; }
  .site-row { grid-template-columns: 18px minmax(0, 1fr) auto; }
}
</style>
